import './App.css';
import MainLayout from './Containers/MainLayout';
import { AuthContextProvider } from './Context/authContext';
import ThemeProvider, { createEmotionCache } from './Context/themeProvider.js';
import 'react-calendar/dist/Calendar.css';
import 'react-perfect-scrollbar/dist/css/styles.css';
import 'react-phone-input-2/lib/style.css';
import 'react-datepicker/dist/react-datepicker.css';
import { AppContextProvider } from './Context/appContext';
import { FormContextProvider } from './Context/formContext';
import { BrowserRouter } from 'react-router-dom';
import { init, browserTracingIntegration, ErrorBoundary, replayIntegration } from '@sentry/react';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { SENTRY_DSN, MIXPANEL_PROJECTID } from './Utils/Constants/constant.js';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider/LocalizationProvider.js';
import { SnackbarProvider, useSnackbar } from 'notistack';
import { ErrorFallback } from './Components/ErrorBoundary';
import mixpanel from 'mixpanel-browser';
import { CacheProvider } from '@emotion/react';
import { useEffect } from 'react';
import { IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DynamicTitleSetter from './Components/DynamicTitle/index.jsx';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

function SnackbarCloseButton({ snackbarKey }) {
  const { closeSnackbar } = useSnackbar();

  return (
    <IconButton onClick={() => closeSnackbar(snackbarKey)}>
      <CloseIcon sx={{ fontSize: '1rem', color: '#fff' }} />
    </IconButton>
  );
}

function App() {
  const queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 5 * 60 * 1000, // 5 minutes - data is considered fresh for 5 minutes
        gcTime: 10 * 60 * 1000, // 10 minutes (formerly cacheTime) - unused data is garbage collected after 10 minutes
        retry: 1, // Retry failed requests once
        refetchOnWindowFocus: false, // Don't refetch on window focus to reduce unnecessary API calls
        refetchOnMount: true, // Refetch when component mounts
        refetchOnReconnect: true, // Refetch when network reconnects
      },
    },
  });

  try {
    init({
      dsn: SENTRY_DSN,
      integrations: [browserTracingIntegration(), replayIntegration()],
      tracesSampleRate: 1.0,
      replaysSessionSampleRate: 0.1,
      replaysOnErrorSampleRate: 1.0,
      sendDefaultPii: true,
    });
  } catch (err) {
    console.error('Sentry initatization error', err);
  }
  try {
    // Mixpanel
    mixpanel.init(MIXPANEL_PROJECTID, {
      debug: true,
      track_pageview: true,
      persistence: 'localStorage',
      autocapture: {
        pageview: 'full-url',
        click: true,
        input: true,
        submit: true,
      },
    });
  } catch (err) {
    console.error('Mixpanel initialization error', err);
  }

  // MUI Cache mechanism
  const clientSideEmotionCache = createEmotionCache();

  useEffect(() => {
    const isWindows = navigator.platform.startsWith('Win');

    if (!isWindows) {
      console.log('Non-Windows platform — skipping zoom detection');
      return;
    }

    const detectZoomLevel = () => {
      const ratio = window.devicePixelRatio || 1;
      return Math.round(ratio * 100); // 100 = normal, 125 = 125% zoom, etc.
    };

    const updateZoomClass = () => {
      const zoom = detectZoomLevel();

      console.log('Detected Zoom Level:', zoom + '%');

      // Remove old zoom classes
      document.body.classList.remove('scale-100', 'scale-125', 'scale-150');

      let scaleClass = 'scale-100';
      if (zoom >= 145) {
        scaleClass = 'scale-150';
      } else if (zoom >= 115) {
        scaleClass = 'scale-125';
      }

      document.body.classList.add(scaleClass);
      console.log('Applied class to <body>:', scaleClass);
    };

    // Initial call
    updateZoomClass();

    // Update dynamically on resize (or zoom)
    window.addEventListener('resize', updateZoomClass);

    return () => {
      window.removeEventListener('resize', updateZoomClass);
    };
  }, []);

  return (
    <SnackbarProvider autoHideDuration={5000} action={(snackbarKey) => <SnackbarCloseButton snackbarKey={snackbarKey} />}>
      <ErrorBoundary
        fallback={({ error, resetError }) => {
          return (
            <ErrorFallback
              error={error.message}
              resetErrorBoundary={() => {
                resetError();
              }}
            />
          );
        }}
      >
        <QueryClientProvider client={queryClient}>
          <LocalizationProvider dateAdapter={AdapterMoment}>
            <BrowserRouter>
              <AppContextProvider>
                <FormContextProvider>
                  <AuthContextProvider>
                    <CacheProvider value={clientSideEmotionCache}>
                      <ThemeProvider>
                        <div className="App">
                        <DynamicTitleSetter/>
                          <MainLayout />
                        </div>
                      </ThemeProvider>
                    </CacheProvider>
                  </AuthContextProvider>
                </FormContextProvider>
              </AppContextProvider>
            </BrowserRouter>
          </LocalizationProvider>
        </QueryClientProvider>
      </ErrorBoundary>
    </SnackbarProvider>
  );
}

export default App;
